import { useContext, useEffect, useState } from "react";
import { Box, Breadcrumbs, Checkbox, FormControl, FormHelperText, InputLabel, MenuItem, Modal, Pagination, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography } from "@mui/material";
import { Container, MainContainer } from "../../../../components";
import { Menu } from '../Menu/Menu';
import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import SEARCH_ICON from '../../../../assets/images/search-icon.svg';
import APPROVED_ICON from '../../../../assets/images/approved.svg';
import REJECTED_ICON from '../../../../assets/images/rejected.svg';
import RadiusButton from '../../../../components/RadiusButton';
import dayjs from '../../../../config/dayjs';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { getRequiredData } from "../../../../services/extendedTimeService";
import { changeDisabledStatus, getListForUnlockCandidate } from "../../../../services/candidateService";
import { useToaster } from "../../../../providers/ToasterProvider";
import { LoadingButton } from "@mui/lab";
import { ProjectContext } from "../../../../providers/projectProvider";
import { getListByProjectMasterWithPagination, unlockCandidateByIds, approveRequestedCandidateUnlock, rejectRequestedCandidateUnlock } from "../../../../services/unlockCandidateService";


const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT);
const disableSchema = z.object({
    reason: z.string().min(1, { message: 'Disable reason is required.' }),
});

export const UnlockCandidate = () => {

    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);

    const defaultDrive = { id: 0, driveName: 'None' };
    const [selectedDrive, setSelectedDrive] = useState(defaultDrive);
    const defaultCourse = { id: 0, courseName: 'None' };
    const [selectedCourse, setSelectedCourse] = useState(defaultCourse);
    const defaultBatch = { id: 0, batchName: 'None' };
    const [selectedBatch, setSelectedBatch] = useState(defaultBatch);
    const defaultExamDate = { id: 0, batchId: 0, courseId: 0, driveId: 0, examDate: 'None', displayExamDate: 'None' };
    const [selectedExamDate, setSelectedExamDate] = useState(defaultExamDate);
    const defaultExamHall = { id: 0, projectMasterId: 0, hallName: 'All' };
    const [selectedExamHall, setSelectedExamHall] = useState(defaultExamHall);

    const [drives, setDrives] = useState([defaultDrive]);
    const [allCourses, setAllCourses] = useState([]);
    const [courses, setCourses] = useState([defaultCourse]);
    const [allBatches, setAllBatches] = useState([]);
    const [batches, setBatches] = useState([defaultBatch]);
    const [projectMasters, setProjectMasters] = useState([]);
    const [examHalls, setExamHalls] = useState([]);
    const [availableExamHalls, setAvailableExamHalls] = useState([defaultExamHall]);
    const [examDates, setExamDates] = useState([defaultExamDate]);

    const [tabIndex, setTabIndex] = useState(1);
    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [candidates, setCandidates] = useState([]);
    const [requestedCandidates, setRequestedCandidates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [updateCandidates, setUpdateCandidates] = useState(false);
    const [timerId, setTimerId] = useState(null);
    const [generalSelected, setGeneralSelected] = useState(false);
    const statusOptions = ['ALL', 'REQUESTED', 'APPROVED'];
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [disableModal, setDisableModal] = useState({
        isVisible: false,
        candidateId: null,
        status: false,
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getRequiredData();
                if (response.code === 'OK') {
                    setDrives([defaultDrive, ...response.data.drives]);
                    setAllCourses(response.data.courses);
                    setAllBatches(response.data.batches);
                    setProjectMasters(response.data.projectMasters.map(pm => {
                        pm.displayExamDate = dayjs(pm.examDate).format('DD-MM-YYYY')
                        return pm;
                    }));
                    setExamHalls(response.data.examHalls);
                }
            } catch (error) {
                console.log('There is an error while fetching unlock candidates required data', error);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        const filteredCourses = [];

        if (selectedDrive.id) {
            for (let projectMaster of projectMasters) {
                if (projectMaster.driveId === selectedDrive.id) {
                    const course = allCourses.find((c) => c.id === projectMaster.courseId);
                    if (course && !filteredCourses.some((c) => c.id === course.id)) {
                        filteredCourses.push(course);
                    }
                }
            }
        }
        setCourses([defaultCourse, ...filteredCourses]);
        setSelectedCourse(defaultCourse);
        setValue('course', 0);
        setBatches([defaultBatch]);
        setSelectedBatch(defaultBatch);
        setValue('batch', 0);

        setExamDates([defaultExamDate]);
        setSelectedExamDate(defaultExamDate);
        setValue('examDate', 0);
        setAvailableExamHalls([defaultExamHall]);
        setSelectedExamHall(defaultExamHall);
        setValue('examHall', 0);
    }, [selectedDrive]);

    useEffect(() => {
        const filteredBatches = [];

        if (selectedDrive.id && selectedCourse.id) {
            for (let projectMaster of projectMasters) {
                if (projectMaster.driveId === selectedDrive.id && projectMaster.courseId === selectedCourse.id) {
                    const batch = allBatches.find((b) => b.id === projectMaster.batchId);
                    if (batch && !filteredBatches.some((b) => b.id === batch.id)) {
                        filteredBatches.push(batch);
                    }
                }
            }
        }
        setBatches([defaultBatch, ...filteredBatches]);
        setSelectedBatch(defaultBatch);
        setValue('batch', 0);

        setExamDates([defaultExamDate]);
        setSelectedExamDate(defaultExamDate);
        setValue('examDate', 0);
        setAvailableExamHalls([defaultExamHall]);
        setSelectedExamHall(defaultExamHall);
        setValue('examHall', 0);
    }, [selectedCourse]);

    useEffect(() => {
        if (selectedDrive.id && selectedCourse.id && selectedBatch.id) {
            const dates = projectMasters.filter((pm) => pm.driveId === selectedDrive.id && pm.courseId === selectedCourse.id && pm.batchId === selectedBatch.id);
            setExamDates([defaultExamDate, ...dates]);
            setSelectedExamDate(defaultExamDate);
            setValue('examDate', 0);
        }
    }, [selectedDrive, selectedCourse, selectedBatch]);

    useEffect(() => {
        if (tabIndex === 0) {
            if (selectedExamDate && selectedExamDate.id) {
                const fetchCandidates = async () => {
                    try {
                        const response = await getListForUnlockCandidate(selectedExamDate.id, selectedExamHall.id, searchTerm.trim(), currentPage, limit);
                        if (response.code === 'OK') {
                            const candidates = response.data?.rows ?? [];
                            setCandidates(candidates.map(candidate => {
                                candidate.isSelected = false;
                                return candidate;
                            }));
                            const count = response.data.count ?? 1;
                            const max = Math.ceil(count / limit);
                            setMaxPages(max);
                            setGeneralSelected(false);
                        }
                    } catch (error) {
                        console.log('There is an error while getting candidates', error);
                    }
                }
                const eHalls = examHalls.filter((eh) => eh.projectMasterId === selectedExamDate.id);
                setAvailableExamHalls([defaultExamHall, ...eHalls]);

                fetchCandidates();
            } else {
                setCandidates([]);
                setMaxPages(1);
                setCurrentPage(1);
                setSearchTerm('');
                setGeneralSelected(false);
                setSelectedExamHall(defaultExamHall);
                setAvailableExamHalls([defaultExamHall]);
            }
        } else {
            if (selectedExamDate?.id) {
                const fetchRequestedUnlockCandidatesList = async () => {
                    try {
                        const status = selectedStatus === 'ALL' ? null : selectedStatus;
                        const response = await getListByProjectMasterWithPagination(selectedExamDate.id, selectedExamHall.id, searchTerm.trim(), currentPage, limit, status);
                        if (response.code === 'OK') {
                            const candidates = response.data?.rows ?? [];
                            setRequestedCandidates(candidates.map(candidate => {
                                candidate.isSelected = false;
                                return candidate;
                            }));

                            const eHalls = examHalls.filter((eh) => eh.projectMasterId === selectedExamDate.id);
                            setAvailableExamHalls([defaultExamHall, ...eHalls]);
                            const count = response.data.count ?? 1;
                            const max = Math.ceil(count / limit);
                            setMaxPages(max);
                            setGeneralSelected(false);
                        }
                    } catch (error) {
                        console.log('There is an error while getting candidates', error);
                    }
                }
                fetchRequestedUnlockCandidatesList();
            } else {
                setRequestedCandidates([]);
                setMaxPages(1);
                setCurrentPage(1);
                setSearchTerm('');
                setGeneralSelected(false);
                setSelectedStatus('ALL');
                setSelectedExamHall(defaultExamHall);
                setAvailableExamHalls([defaultExamHall]);
            }
        }
    }, [selectedExamDate, updateCandidates, tabIndex]);

    const handleExamHallChange = (e, value) => {
        const examHall = availableExamHalls.find((eh) => eh.id === value.props.value);
        setSelectedExamHall(examHall);
        setValue('examHall', value.props.value);
        setUpdateCandidates(!updateCandidates);
    }

    const updateTimeSchema = z.object({
        drive: z.number().gte(1),
        course: z.number().gte(1),
        batch: z.number().gte(1),
        examDate: z.number().gte(1),
        examHall: z.number(),
    });

    const {
        formState: { errors },
        trigger,
        setValue,
    } = useForm({
        resolver: zodResolver(updateTimeSchema),
    });
    const {
        register,
        handleSubmit,
        formState: { disabledErrors },
        trigger: disabledTrigger,
        reset,
    } = useForm({
        resolver: zodResolver(disableSchema),
    });

    const columns = [
        { id: 'rollNo', label: 'Roll No', minWidth: 140 },
        { id: 'userName', label: 'Username', minWidth: 140 },
        { id: 'candidateName', label: 'Candidate Name', minWidth: 140 },
        { id: 'applicationNo', label: 'Application No', minWidth: 140 },
        { id: 'failedLoginAttempts', label: 'Failed Login Attempts', minWidth: 140 },
        { id: 'selected', label: '', minWidth: 200 },
    ];
    const requestedColumns = [
        { id: 'rollNo', label: 'Roll No', minWidth: 140 },
        { id: 'userName', label: 'Username', minWidth: 140 },
        { id: 'candidateName', label: 'Candidate Name', minWidth: 140 },
        { id: 'applicationNo', label: 'Application No', minWidth: 140 },
        { id: 'status', label: 'Status', minWidth: 200 },
    ];

    const handleGeneralSelectedChange = (event, value) => {
        setGeneralSelected(value);
        if (tabIndex === 0) {
            setCandidates(
                candidates.map((candidate) => {
                    if (!candidate.examEnded) {
                        candidate.isSelected = value;
                    }
                    return candidate;
                })
            );
        } else {
            setRequestedCandidates(
                requestedCandidates.map((candidate) => {
                    if (!candidate.examEnded && candidate.status === 'REQUESTED') {
                        candidate.isSelected = value;
                    }
                    return candidate;
                })
            );
        }
    }

    const handleCandidateSelectedChange = (rowIndex, value) => {
        let allSelected = true;
        setCandidates(
            candidates.map((candidate, index) => {
                if (index === rowIndex) {
                    candidate.isSelected = value;
                }
                if (allSelected && !candidate.isSelected) {
                    allSelected = false;
                }
                return candidate;
            })
        );
        setGeneralSelected(allSelected);
    }

    const hideDisableModal = () => {

        reset();
        setDisableModal({
            isVisible: false,
            candidateId: null,
            status: false,
        })
    }
    const handleConfirmDisable = async (value) => {
        try {
            const reason = value.reason;
            const result = await changeDisabledStatus(disableModal.candidateId, disableModal.status, reason);
            if (result.code === "OK") {
                addNotification('Disabled status updated successfully.', 'success')
                setCandidates(
                    candidates.map((candidate) => {
                        if (candidate.id === disableModal.candidateId) {
                            candidate.isDisabled = disableModal.status;
                        }
                        return candidate;
                    })
                )
                setDisableModal({
                    isVisible: false,
                    candidateId: null,
                    status: false
                })
                reset();
            }
        } catch (error) {
            console.log('There is an error while changing disabled status', error);
            addNotification('Disabled status does not changed', 'danger');
        }
    }

    const renderTableCell = (columnId, rowIndex) => {
        const row = candidates[rowIndex];
        switch (columnId) {
            case 'selected':
                return <Checkbox
                    onChange={(e, value) => handleCandidateSelectedChange(rowIndex, value)}
                    checked={row.isSelected}
                    disabled={row.examEnded}
                />
            default:
                return row[columnId];
        }
    }

    const renderRequestedTableCell = (columnId, rowIndex) => {
        const row = requestedCandidates[rowIndex];
        switch (columnId) {
            case 'status':
                return row.status === 'APPROVED'
                    ? <img src={APPROVED_ICON} alt='' />
                    : row.status === 'REJECTED'
                        ? <img src={REJECTED_ICON} alt='' />
                        : <Checkbox onChange={(e, value) => {
                            let allSelected = true;
                            setRequestedCandidates(
                                requestedCandidates.map((candidate, index) => {
                                    if (index === rowIndex) {
                                        candidate.isSelected = value;
                                    }
                                    if (allSelected && !candidate.isSelected) {
                                        allSelected = false;
                                    }
                                    return candidate;
                                })
                            )
                            setGeneralSelected(allSelected);
                        }} checked={row.isSelected} />
            case 'rollNo':
                return row['candidate.rollNo'];
            case 'userName':
                return row['candidate.userName'];
            case 'candidateName':
                return row['candidate.candidateName'];
            case 'applicationNo':
                return row['candidate.applicationNo'];
            default:
                return row[columnId];
        }
    }

    const handleSearchTermChange = (e) => {
        setSearchTerm(e.target.value);
        if (timerId) {
            clearTimeout(timerId);
        }
        let timer = setTimeout(() => {
            setCurrentPage(1);
            setUpdateCandidates(!updateCandidates);
        }, 400);
        setTimerId(timer);
    }

    const handlePageChange = (value) => {
        if (currentPage === value) return;
        setCurrentPage(value);
        setUpdateCandidates(!updateCandidates);
    }

    const handleUnlockCandidate = async () => {
        const projectMasterId = selectedExamDate?.id;
        if (!projectMasterId) return;

        const selectedCandidates = candidates.filter((candidate) => candidate.isSelected);
        if (!selectedCandidates.length) {
            addNotification('Please select candidates.', 'information');
            return;
        }
        setIsUnlocking(true);
        try {
            const candidateIds = selectedCandidates.map((candidate) => candidate.id);
            const res = await unlockCandidateByIds(projectMasterId, candidateIds);
            if (res.code === 'OK') {
                addNotification('Candidates unlocked successfully.', 'success');
                setGeneralSelected(false);
                setUpdateCandidates(!updateCandidates);
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === "E_INTERNAL_SERVER_ERROR") {
                addNotification('Candidates not unlocked.', 'danger');
            } else if (code === "E_BAD_REQUEST") {
                addNotification('Candidates not unlocked.', 'danger');
            }
            console.log('There is an error while unlocking candidates', error);
        } finally {
            setIsUnlocking(false);
        }
    }

    const handleApproveCandidateUnlock = async () => {
        const unlockCandidate = requestedCandidates.filter((candidate) => candidate.isSelected);
        const unlockCandidateIds = unlockCandidate.map((at) => at.id);
        if (!unlockCandidateIds.length) {
            addNotification('Please select candidates.', 'information');
            return;
        }
        setIsApproving(true);
        try {
            const response = await approveRequestedCandidateUnlock(unlockCandidateIds);
            if (response.code === 'OK') {
                addNotification('Requested candidate unlock approved successfully.', 'success');
                setUpdateCandidates(!updateCandidates);
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'E_INTERNAL_SERVER_ERROR') {
                addNotification('All requests are not approved.', 'danger');
            }
            console.log('There is an error while approving candidate unlock requests.', error);
        } finally {
            setIsApproving(false);
        }
    }

    const handleRejectCandidateUnlock = async () => {
        const unlockCandidate = requestedCandidates.filter((candidate) => candidate.isSelected);
        const unlockCandidateIds = unlockCandidate.map((at) => at.id);
        if (!unlockCandidateIds.length) {
            addNotification('Please select candidates.', 'information');
            return;
        }
        setIsRejecting(true);
        try {
            const response = await rejectRequestedCandidateUnlock(unlockCandidateIds);
            if (response.code === 'OK') {
                addNotification('Requested candidate unlock rejected successfully.', 'success');
                setUpdateCandidates(!updateCandidates);
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'E_INTERNAL_SERVER_ERROR') {
                addNotification('All requests are not rejected.', 'danger');
            }
            console.log('There is an error while rejecting candidate unlock requests.', error);
        } finally {
            setIsRejecting(false);
        }
    }

    return <MainContainer>
        <Menu>
            <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                <Typography color="text.primary" sx={{
                    fontWeight: 400,
                    textTransform: 'capitalize'
                }}>
                    {projectDetails?.projectName}
                </Typography>
                <Box sx={{ display: 'flex', alignContent: 'center' }}>
                    <img src={ARROW} alt=''></img>
                </Box>
                <Typography color="text.primary" sx={{
                    fontWeight: 400,
                    textTransform: 'capitalize'
                }}>
                    Unlock Candidate
                </Typography>
                <Box sx={{ display: 'flex', alignContent: 'center' }}>
                    <img src={ARROW_OPEN} alt=''></img>
                </Box>
            </Breadcrumbs>
        </Menu>

        <Container sx={{
            display: 'flex',
            flexDirection: 'column'
        }}>
            <h1 className="title" style={{ marginBottom: '12px' }}>Unlock Candidate</h1>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                py: 3,
            }}>
                <FormControl className='form-field' sx={{ width: 205 }}>
                    <InputLabel id="driveName">Drive Name</InputLabel>
                    <Select
                        labelId="driveName"
                        id="drive"
                        label='Drive Name'
                        name='drive'
                        error={Boolean(errors.drive)}
                        size='small'
                        onBlur={() => trigger('drive')}
                        value={selectedDrive?.id ?? 0}
                        onChange={(e, event) => {
                            const drive = drives.find((d) => d.id === event.props.value);
                            setSelectedDrive(drive);
                            setValue('drive', event.props.value);
                        }}
                    >
                        {
                            drives.map(
                                (drive) => <MenuItem key={drive.id} value={drive.id}>
                                    {drive.driveName}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {errors.drive?.message
                        && <FormHelperText sx={{ color: 'error.main' }}>Please select drive name.</FormHelperText>
                    }
                </FormControl>

                <FormControl className='form-field' sx={{ width: 205 }}>
                    <InputLabel id="courseName">Course Name</InputLabel>
                    <Select
                        labelId="courseName"
                        id="course"
                        label='Course Name'
                        name='course'
                        error={Boolean(errors.course)}
                        size='small'
                        onBlur={() => trigger('course')}
                        value={selectedCourse?.id ?? 0}
                        onChange={(e, event) => {
                            const course = courses.find((c) => c.id === event.props.value);
                            setSelectedCourse(course);
                            setValue('course', event.props.value);
                        }}
                    >
                        {
                            courses.map(
                                (course) => <MenuItem key={course.id} value={course.id}>
                                    {course.courseName}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {errors.course?.message
                        && <FormHelperText sx={{ color: 'error.main' }}>Please select course name.</FormHelperText>
                    }
                </FormControl>

                <FormControl className='form-field' sx={{ width: 205 }}>
                    <InputLabel id="batchName">Batch Name</InputLabel>
                    <Select
                        labelId="batchName"
                        id="batch"
                        label='Batch Name'
                        name='batch'
                        error={Boolean(errors.batch)}
                        size='small'
                        onBlur={() => trigger('batch')}
                        value={selectedBatch?.id ?? 0}
                        onChange={(e, event) => {
                            const batch = batches.find((b) => b.id === event.props.value);
                            setSelectedBatch(batch);
                            setValue('batch', event.props.value);
                        }}
                    >
                        {
                            batches.map(
                                (batch) => <MenuItem key={batch.id} value={batch.id}>
                                    {batch.batchName}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {errors.batch?.message
                        && <FormHelperText sx={{ color: 'error.main' }}>Please select batch name.</FormHelperText>
                    }
                </FormControl>

                <FormControl className='form-field' sx={{ width: 205 }}>
                    <InputLabel id="examDate">Exam Date</InputLabel>
                    <Select
                        labelId="examDate"
                        id="examDate"
                        label='Exam Date'
                        name='examDate'
                        error={Boolean(errors.examDate)}
                        size='small'
                        onBlur={() => trigger('examDate')}
                        value={selectedExamDate?.id ?? 0}
                        onChange={(e, event) => {
                            const examDate = examDates.find((ed) => ed.id === event.props.value);
                            setSelectedExamDate(examDate);
                            setValue('examDate', event.props.value);
                        }}
                    >
                        {
                            examDates.map(
                                (ed) => <MenuItem key={ed.id} value={ed.id}>
                                    {ed.displayExamDate}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {errors.examDate?.message
                        && <FormHelperText sx={{ color: 'error.main' }}>Please select exam date.</FormHelperText>
                    }
                </FormControl>
                {
                    selectedExamDate.id
                        ? <FormControl className='form-field' sx={{ width: 205 }}>
                            <InputLabel id="examHall">Exam Hall</InputLabel>
                            <Select
                                labelId="examHall"
                                id="examHall"
                                label='Exam Hall'
                                name='examHall'
                                // error={Boolean(errors.examHall)}
                                size='small'
                                // onBlur={() => trigger('examHall')}
                                value={selectedExamHall?.id ?? 0}
                                onChange={handleExamHallChange}
                            >
                                {
                                    availableExamHalls.map(
                                        (ed) => <MenuItem key={ed.id} value={ed.id}>
                                            {ed.hallName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                        </FormControl>
                        : null
                }
            </Box>

            <Tabs variant='scrollable' value={tabIndex} onChange={(e, value) => {
                setSearchTerm('');
                setSelectedStatus('all');
                setTabIndex(value)
            }}>
                <Tab
                    sx={{
                        textTransform: 'capitalize',
                        fontSize: 15,
                        fontWeight: 600,
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                    }}
                    label='Unlock'
                    id='unlockTabPanel'
                    aria-controls='unlockTabPanelWrapper'
                />
                <Tab
                    sx={{
                        textTransform: 'capitalize',
                        fontSize: 15,
                        fontWeight: 600,
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                    }}
                    label='Requested'
                    id='requestedTabPanel'
                    aria-controls='requestedTabPanelWrapper'
                />
            </Tabs>
            <Paper sx={{
                borderRadius: 3,
                bgcolor: 'transparent',
                border: '1px solid !important',
                borderColor: '#3F41D1 !important',
                borderTopLeftRadius: tabIndex ? 12 : 0,
                boxShadow: 'unset',
                flex: 1,
                maxWidth: 'calc(100vw - 140px)',
                height: 'auto',
                overflow: 'auto',
            }}>
                {tabIndex === 0 && <Box
                    role='tabpanel'
                    id='unlockTabPanelWrapper'
                    sx={{
                        py: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    area-labelledby='unlockTabPanelWrapper'
                >
                    {tabIndex === 0 && <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 3,
                        px: 2,
                        height: 46
                    }}>
                        <div className='search-wrapper' style={{ marginLeft: 'auto', marginRight: 'unset' }}>
                            <input type='text' className='search-box' value={searchTerm} onChange={handleSearchTermChange} placeholder='Search...' />
                            <img className='search-icon' src={SEARCH_ICON} alt='' />
                        </div>
                    </Box>}


                    <TableContainer sx={{
                        pb: 1,
                        my: 1,
                        border: '1px solid',
                        borderColor: 'grey.500',
                        maxHeight: 'inherit',
                        bgcolor: 'background.base',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => {
                                        switch (column.id) {
                                            case 'selected':
                                                return <TableCell
                                                    sx={{
                                                        color: 'primary.main',
                                                        p: 1.5,
                                                        '.MuiCheckbox-root': { p: 1 }
                                                    }}
                                                    key={column.id}
                                                    align='center'
                                                    style={{ minWidth: column.minWidth }}
                                                >
                                                    <Checkbox checked={generalSelected} onChange={handleGeneralSelectedChange} />
                                                </TableCell>
                                            default:
                                                return <TableCell
                                                    sx={{
                                                        color: 'primary.main',
                                                        p: 1.5,
                                                    }}
                                                    key={column.id}
                                                    align='center'
                                                    style={{ minWidth: column.minWidth }}
                                                >
                                                    {column.label}
                                                </TableCell>
                                        }
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!candidates?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={11} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : candidates.map((row, rowIndex) => {
                                        return (
                                            <TableRow hover={false} role="checkbox" key={rowIndex}>
                                                {columns.map((column, index) => {
                                                    return <TableCell key={column.id} align='center' sx={{
                                                        px: 1.5,
                                                        py: 0.75
                                                    }}>
                                                        {renderTableCell(column.id, rowIndex)}
                                                    </TableCell>
                                                })}
                                            </TableRow>
                                        );
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 2,
                    }}>
                        <LoadingButton
                            sx={{ borderRadius: 40 }}
                            disabled={!selectedDrive.id || !selectedCourse.id || !selectedBatch.id || !selectedExamDate.id || !candidates?.length}
                            color='secondary'
                            variant='contained'
                            onClick={handleUnlockCandidate}
                            loading={isUnlocking}
                        >
                            Unlock
                        </LoadingButton>
                        {
                            candidates?.length
                                ? <Pagination sx={{ mt: 'auto' }} count={maxPages} page={currentPage} onChange={(e, value) => handlePageChange(value)} shape="rounded" />
                                : null
                        }
                    </Box>

                </Box>}
                {tabIndex === 1 && <Box
                    role='tabpanel'
                    id='requestedTabPanelWrapper'
                    sx={{ overflow: 'auto', py: 2, height: '100%' }}
                    area-labelledby='requestedTabPanelWrapper'
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: 2,
                        gap: 2,
                    }}>
                        <FormControl className='form-field' sx={{ width: 205 }}>
                            <InputLabel id="extendedTimeStatus">Status</InputLabel>
                            <Select
                                labelId="extendedTimeStatus"
                                id="status"
                                label='Status'
                                name='status'
                                size='small'
                                sx={{ textTransform: 'capitalize' }}
                                // error={Boolean(errors.batch)}
                                // onBlur={() => trigger('batch')}
                                value={selectedStatus}
                                onChange={(e, event) => {
                                    setSelectedStatus(event.props.value);
                                    setUpdateCandidates(!updateCandidates);
                                }}
                            >
                                {
                                    statusOptions.map(
                                        (status) => <MenuItem sx={{ textTransform: 'capitalize' }} key={status} value={status}>
                                            {status}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                        </FormControl>
                        <div className='search-wrapper' style={{ marginRight: 'unset' }}>
                            <input type='text' className='search-box' value={searchTerm} onChange={handleSearchTermChange} placeholder='Search...' />
                            <img className='search-icon' src={SEARCH_ICON} alt='' />
                        </div>
                    </Box>
                    <Paper sx={{
                        width: '100%',
                        overflow: 'auto',
                        bgcolor: 'background.base',
                        maxHeight: 'calc(100% - 125px)',
                        maxWidth: 'calc(100vw - 140px)',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        mb: 3,
                        mt: 2
                    }}>
                        <TableContainer sx={{
                            maxHeight: 'inherit',
                            maxWidth: 'inherit',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.500',
                        }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {requestedColumns.map((column) => {
                                            switch (column.id) {
                                                case 'status':
                                                    return <TableCell
                                                        sx={{
                                                            color: 'primary.main',
                                                            p: 1.5,
                                                            '.MuiCheckbox-root': { p: 1 }
                                                        }}
                                                        key={column.id}
                                                        align='center'
                                                        style={{ minWidth: column.minWidth }}
                                                    >
                                                        <Checkbox checked={generalSelected} onChange={handleGeneralSelectedChange} />
                                                    </TableCell>
                                                default:
                                                    return <TableCell
                                                        sx={{
                                                            color: 'primary.main',
                                                            p: 1.5,
                                                        }}
                                                        key={column.id}
                                                        align='center'
                                                        style={{ minWidth: column.minWidth }}
                                                    >
                                                        {column.label}
                                                    </TableCell>
                                            }
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!requestedCandidates?.length
                                        ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }} >
                                            <TableCell colSpan={5} sx={{ border: 'none', textAlign: 'center' }}>
                                                No records available
                                            </TableCell>
                                        </TableRow>
                                        : requestedCandidates.map((row, rowIndex) => {
                                            return (
                                                <TableRow hover={false} role="checkbox" key={rowIndex} >
                                                    {requestedColumns.map((column, index) => {
                                                        return <TableCell key={column.id} align='center' sx={{
                                                            px: 1.5,
                                                            py: 1
                                                        }}>
                                                            {renderRequestedTableCell(column.id, rowIndex)}
                                                        </TableCell>
                                                    })}
                                                </TableRow>
                                            );
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        px: 2,
                        gap: 1,
                    }}>
                        {selectedStatus !== 'approved' && <LoadingButton
                            sx={{ borderRadius: 40 }}
                            disabled={!selectedDrive.id || !selectedCourse.id || !selectedBatch.id || !selectedExamDate.id || !requestedCandidates?.length}
                            color='secondary'
                            variant='contained'
                            onClick={handleApproveCandidateUnlock}
                            loading={isApproving}
                        >
                            Approve
                        </LoadingButton>}
                        {selectedStatus !== 'approved' && <LoadingButton
                            sx={{ borderRadius: 40 }}
                            disabled={!selectedDrive.id || !selectedCourse.id || !selectedBatch.id || !selectedExamDate.id || !requestedCandidates?.length}
                            color='secondary'
                            variant='contained'
                            onClick={handleRejectCandidateUnlock}
                            loading={isRejecting}
                        >
                            Reject
                        </LoadingButton>}
                        {
                            requestedCandidates?.length
                                ? <Pagination sx={{ ml: 'auto', mt: 'auto' }} count={maxPages} page={currentPage} onChange={(e, value) => handlePageChange(value)} shape="rounded" />
                                : null
                        }
                    </Box>
                </Box>}
            </Paper>

            <Modal open={disableModal.isVisible} className='backdrop' hideBackdrop={true} onClose={hideDisableModal}>
                <Box className='modal-container' component='form' autoComplete='off' sx={{
                    maxWidth: 480,
                    p: 3,
                }} onSubmit={handleSubmit(handleConfirmDisable)}>
                    <Typography variant='h6' sx={{
                        fontWeight: 400,
                        mb: 1.5,
                        textAlign: 'center'
                    }}>
                        Disabled reason:
                    </Typography>
                    <TextField
                        size='small'
                        variant="outlined"
                        placeholder='Disabled reason'
                        {...register('reason')}
                        error={Boolean(disabledErrors?.reason)}
                        helperText={disabledErrors?.reason?.message}
                        onBlur={() => disabledTrigger('reason')}
                        sx={{
                            width: '100%',
                            bgcolor: 'background.base',
                            mb: 2.5
                        }}
                    />

                    <div className='actions-container' style={{ justifyContent: 'center', gap: '40px' }}>
                        <RadiusButton variant='outlined' onClick={hideDisableModal}>
                            Cancel
                        </RadiusButton>
                        <RadiusButton variant='contained' type='submit'>
                            Disable
                        </RadiusButton>
                    </div>
                </Box>
            </Modal>

        </Container>

    </MainContainer >
}
